import { useMemo } from 'react';
import type {
    TiersTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
    AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';

export interface ProcessedAddress {
    numeroRue: string;
    appartement: string;
    complement: string;
    boitePostale: string;
    codePostal: string;
    ville: string;
    pays: string;
}

export interface AddressLogicResult {
    mainAddress: ProcessedAddress;
    billingAddress?: ProcessedAddress;
    isBillingAddressSame: boolean;
    hasBillingAddress: boolean;
}

/**
 * Transforme une adresse API en adresse processée pour l'affichage
 */
const processAddress = (
    address: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
): ProcessedAddress => ({
    numeroRue: address.adresse1 || 'Donnée manquante',
    appartement: address.adresse2 || ' - ',
    complement: address.adresse3 || ' - ',
    boitePostale: address.adresse4 || ' - ',
    codePostal: address.codePostal || 'Donnée manquante',
    ville: address.ville || 'Donnée manquante',
    pays: address.codePays || 'Donnée manquante',
});

/**
 * Génère une adresse d'erreur avec le message spécifié
 */
const getErrorAddress = (errorMessage: string): ProcessedAddress => ({
    numeroRue: errorMessage,
    appartement: errorMessage,
    complement: errorMessage,
    boitePostale: errorMessage,
    codePostal: errorMessage,
    ville: errorMessage,
    pays: errorMessage,
});

/**
 * Compare deux adresses pour déterminer si elles sont identiques
 */
const areAddressesEqual = (
    address1: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
    address2: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
): boolean => {
    const fields: (keyof AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup)[] = [
        'adresse1',
        'adresse2',
        'adresse3',
        'adresse4',
        'codePostal',
        'ville',
        'codePays',
    ];

    return fields.every((field) => {
        const val1 = (address1[field] || '').toString().trim().toLowerCase();
        const val2 = (address2[field] || '').toString().trim().toLowerCase();
        return val1 === val2;
    });
};

/**
 * Hook pour gérer la logique des adresses d'un prescripteur.
 * Détermine l'adresse principale, l'adresse de facturation et leur similarité.
 */
export const useAddressLogic = (
    prescriber?: TiersTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
): AddressLogicResult =>
    useMemo(() => {
        if (!prescriber?.adresses || prescriber.adresses.length === 0) {
            return {
                mainAddress: getErrorAddress('Aucune adresse trouvée'),
                billingAddress: undefined,
                isBillingAddressSame: true,
                hasBillingAddress: false,
            };
        }

        // Recherche de l'adresse principale (defaut: true)
        const mainAddresses = prescriber.adresses.filter((addr) => addr.defaut === true);
        const mainAddress = mainAddresses.length > 0 ? mainAddresses[0] : null;

        // Recherche de l'adresse de facturation (facturation: true)
        const billingAddress = prescriber.adresses.find((addr) => addr.facturation === true);

        const hasBillingAddress = !!billingAddress;

        // Si pas d'adresse principale, message d'erreur
        if (!mainAddress) {
            return {
                mainAddress: getErrorAddress('Adresse principale non définie'),
                billingAddress: billingAddress ? processAddress(billingAddress) : undefined,
                isBillingAddressSame: false,
                hasBillingAddress,
            };
        }

        // Compare les adresses pour déterminer si elles sont identiques
        const isBillingAddressSame =
            !hasBillingAddress || areAddressesEqual(mainAddress, billingAddress);

        return {
            mainAddress: processAddress(mainAddress),
            billingAddress: hasBillingAddress ? processAddress(billingAddress) : undefined,
            isBillingAddressSame,
            hasBillingAddress,
        };
    }, [prescriber?.adresses]);
